import { createAsyncThunk } from "@reduxjs/toolkit";
import { fetchData } from "../../../services/apiService";
import {
  getLocalTzOffsetMinutes,
  localCalendarDateToYyyymmdd,
} from "../../../services/dateUtils";
import { showSnackbar } from "../../../components/snackbar/Snackbar.slice";

const MERCHANT_PERIODIC_GENERATE_REPORT =
  "merchantPeriodicReport/generateReport";

export const generateReport = createAsyncThunk(
  MERCHANT_PERIODIC_GENERATE_REPORT,
  async (
    {
      merchantID,
      holdingID,
      groupID,
      isoID,
      currency,
      reportStartDate,
      reportEndDate,
      token,
      acquirerData,
      onSuccessCallback = false,
      storeAcquirerId,
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const url = "/merchant-periodic-clearing-settlement";
      // const url = "/report/merchant-periodic-clearing-settlement";
      const maxRetries = 3;
      let attempts = 0;
      let success = false;
      let retryCount = 0;
      let merchantData = null;
      let merchantHolding = null;
      let consolidatedData = null;

      const timestamp = new Date().toISOString();
      const data = {
        version: "04.09.01",
        merchantHoldingID: holdingID !== "" ? holdingID : null,
        merchantGroupID: groupID !== "" ? groupID : null,
        merchantID: merchantID !== "" ? merchantID : null,
        isoID,
        iso: {
          callbackURL:
            "https://webhook.site/dd4eadec-dab5-4397-9754-c45738f2fb9e",
          timeStamp: timestamp,
        },
        fiat: { currencyCode: currency },
        report: {
          // Fechas de calendario local; el back agrupa por día local usando tzOffsetMinutes.
          startDate: localCalendarDateToYyyymmdd(reportStartDate),
          endDate: localCalendarDateToYyyymmdd(reportEndDate),
          tzOffsetMinutes: getLocalTzOffsetMinutes(reportStartDate),
        },
      };

      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        acquirerName: acquirerData.acquirerName,
        acquirerID: storeAcquirerId || acquirerData.acquirerId,
      };

      while (attempts < maxRetries && !success) {
        try {
          attempts++;
          retryCount = attempts;

          const response = await fetchData({
            endpoint: url,
            data,
            headers,
            token,
          });

          const hasDates =
            response?.data?.holding?.dates &&
            Array.isArray(response.data.holding.dates) &&
            response.data.holding.dates.length > 0;

          const hasConsolidated =
            response?.data?.holding?.consolidated &&
            Object.keys(response.data.holding?.consolidated).length > 0;

          if (hasDates || hasConsolidated) {
            if (hasDates) {
              merchantData = response.data.holding.dates;
              merchantHolding = response.data.merchantHolding;
            }
            consolidatedData = hasConsolidated
              ? response.data.holding.consolidated
              : null;
            dispatch(
              showSnackbar({
                message: "Report generated successfully!",
                severity: "success",
              }),
            );
            success = true;
          } else {
            const errorMessage = "No valid data received from server";
            dispatch(
              showSnackbar({
                message: errorMessage,
                severity: "error",
              }),
            );
            throw new Error(errorMessage);
          }
        } catch (error) {
          if (attempts < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
          }
        }
      }

      if (!success) {
        dispatch(
          showSnackbar({
            message: "No transaction for this date",
            severity: "error",
          }),
        );
        return rejectWithValue({
          message: "No transaction for this date" || "Error generating report",
        });
      }

      if (onSuccessCallback) {
        onSuccessCallback();
      }

      return {
        retryCount,
        merchantData,
        merchantHolding,
        consolidatedData,
        currency,
      };
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Error generating report",
      });
    }
  },
);
